import { apiGatewayClient } from '@/common/apis/client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

export interface UserCenterService {
  user_center_id: string;
  active_booking?: boolean;
}

export interface UserCenterServicesResponse {
  data?: UserCenterService[];
}

export const getUserCenterServices = async (params: { user_center_id: string; server_id: number }) => {
  const { data } = await apiGatewayClient.get<UserCenterServicesResponse>('/v1/user-center-services', { params });
  return data;
};

export const toggleOnlineVisitBooking = async (payload: { user_center_id: string; can_booking: '0' | '1' }) => {
  const { data } = await apiGatewayClient.patch('/v1/canbookingon&off', payload);
  return data;
};

export const useOnlineVisitServices = (userCenterId?: string) =>
  useQuery(
    ['doctorHome', 'onlineVisitServices', userCenterId],
    () => getUserCenterServices({ user_center_id: userCenterId!, server_id: 1 }),
    {
      enabled: !!userCenterId,
      staleTime: 60 * 1000,
    },
  );

export const useToggleOnlineVisit = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: toggleOnlineVisitBooking,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries(['doctorHome', 'onlineVisitServices', variables.user_center_id]);
    },
  });
};

// --- ov-booking-guard (پیایلوت: فقط پزشکانِ فلگ‌خورده — بک‌اند: لوتولی) ---
// قرارداد عین سوییچ «مراجعین من» در نلسون‌فرانت (services/bookListApi.ts).

const LUTHULI_PROXY_BASE = '/v1/luthuli';

export type OnlineVisitGuardChat = {
  ticket_id: number;
  book_id?: string | null;
  patient_name?: string | null;
  hours_overdue?: number;
  chat_url?: string;
};

export type OnlineVisitGuardResponse = {
  allowed?: boolean;
  open_count?: number;
  chats?: OnlineVisitGuardChat[];
  future_count?: number;
  can_booking?: number | string;
  verified?: boolean;
};

/** فعال‌سازی با گارد: چت بازِ بی‌پاسخ داشت → allowed:false + جدول چت‌ها (هیچ تغییری نمی‌کند) */
export const enableOnlineVisitGuarded = async (userCenterId: string): Promise<OnlineVisitGuardResponse> => {
  const { data } = await apiGatewayClient.post(
    `${LUTHULI_PROXY_BASE}/online-visit-booking/user-centers/${userCenterId}/enable`,
  );
  return data ?? {};
};

/** غیرفعال‌سازی با شمارش نوبت‌های آینده (future_count برای مودال لغو) */
export const disableOnlineVisitGuarded = async (userCenterId: string): Promise<OnlineVisitGuardResponse> => {
  const { data } = await apiGatewayClient.post(
    `${LUTHULI_PROXY_BASE}/online-visit-booking/user-centers/${userCenterId}/disable`,
  );
  return data ?? {};
};

/** لغو نوبت‌های آینده — همان قرارداد قدیمی از مسیر لوتولی */
export const deleteBooksAfterDeactiveOnlineVisitGuarded = async (userCenterId: string): Promise<unknown> => {
  const { data } = await apiGatewayClient.patch(`${LUTHULI_PROXY_BASE}/delete-book-after-deactive-online-visit`, {
    can_booking: '0',
    user_center_id: userCenterId,
  });
  return data;
};

/** بعد از تغییر از مسیر گارد، کش وضعیت (user-center-services) را هم تازه کن — مثل mutation مسیر قدیمی */
export const useInvalidateOnlineVisitServices = () => {
  const queryClient = useQueryClient();

  return (userCenterId: string) =>
    queryClient.invalidateQueries(['doctorHome', 'onlineVisitServices', userCenterId]);
};
